import { useRef, useState } from "react";
import type { FormEvent } from "react";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { siteInfo } from "@/mocks/site";

const FORM_ID = "dahgjsbp14a1h3mr6m50";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dahgjsbp14a1h3mr6m50";

const contactCards = [
  {
    icon: "ri-mail-line",
    title: "业务与合作",
    value: siteInfo.email,
    href: `mailto:${siteInfo.email}`,
    desc: "内容合作、栏目共建与品牌沟通。",
  },
  {
    icon: "ri-megaphone-line",
    title: "线索与纠错",
    value: siteInfo.tipEmail,
    href: `mailto:${siteInfo.tipEmail}`,
    desc: "提供新进展、补充材料或指出内容错误。",
  },
  {
    icon: "ri-weibo-line",
    title: "社交账号",
    value: siteInfo.weibo,
    href: "#contact-form",
    desc: "私信我们，建议附上可核实的公开来源。",
  },
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "联系我们 - 蝌蚪爆料",
    description: "通过表单或邮箱联系蝌蚪爆料，提供线索、补充材料或指出内容错误。",
    url: "https://dfjor.cn/contact",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://dfjor.cn/" },
      { "@type": "ListItem", position: 2, name: "联系我们", item: "https://dfjor.cn/contact" },
    ],
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const formData = new FormData(formElement);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      // 静默处理疑似自动化提交，返回与正常提交一致的反馈
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    formData.delete("website_alt");
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") payload.append(key, value);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam|form data is spam/i.test(serverMsg);
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        formElement.reset();
        return;
      }

      setStatus("error");
      setFormError(serverMsg || "提交没有成功，请稍后再试，或直接发送邮件与我们联系。");
    } catch {
      setStatus("error");
      setFormError("网络似乎不太稳定，提交失败了。请稍后重试，或直接发送邮件给我们。");
    }
  };

  return (
    <>
      <Seo
        title="联系我们 - 蝌蚪爆料 | 线索提供与内容纠错"
        description="联系蝌蚪爆料：提供热点线索、补充可核实材料，或指出内容错误。也可以通过业务邮箱沟通合作与栏目共建。"
        keywords="蝌蚪爆料,联系我们,线索提供,内容纠错,商务合作"
        path="/contact"
        jsonLd={structuredData}
      />

      <section className="border-b border-background-200 bg-background-100 px-4 py-10 md:px-6 md:py-12 lg:px-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
        <h1 className="mt-5 max-w-3xl font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
          联系我们：把你看知道的新进展告诉蝌蚪爆料
        </h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-600">
          无论是一条线索、一份可核实的材料，还是我们写错的一个时间点，都欢迎告诉我们。
          附带公开来源的线索，处理起来会更快。
        </p>
      </section>

      <section className="px-4 py-12 md:px-6 md:py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {contactCards.map((card) => (
            <a
              key={card.title}
              href={card.href}
              className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className={`${card.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">{card.title}</h2>
              <p className="mt-1 text-sm font-medium text-primary-700">{card.value}</p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{card.desc}</p>
            </a>
          ))}
        </div>
      </section>

      <section id="contact-form" className="scroll-mt-24 px-4 pb-16 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">提交线索或纠错</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              填写下面的表单即可。我们会先核对信息的公开来源，再决定是否更新到对应事件的时间线上。
            </p>
            <ul className="mt-6 space-y-3 text-sm text-foreground-700">
              <li className="flex gap-2.5">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center text-secondary-600">
                  <i className="ri-check-line text-base" aria-hidden="true"></i>
                </span>
                尽量附上可公开访问的链接或截图说明。
              </li>
              <li className="flex gap-2.5">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center text-secondary-600">
                  <i className="ri-check-line text-base" aria-hidden="true"></i>
                </span>
                说明涉及的是哪个事件，方便我们定位。
              </li>
              <li className="flex gap-2.5">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center text-secondary-600">
                  <i className="ri-check-line text-base" aria-hidden="true"></i>
                </span>
                我们不会公开你的个人信息，除非你明确同意。
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            {status === "success" ? (
              <div className="flex flex-col items-center py-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-checkbox-circle-line text-3xl" aria-hidden="true"></i>
                </span>
                <h3 className="mt-5 font-heading text-xl font-bold text-foreground-950">已经收到你的信息</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-foreground-600">
                  感谢你的线索或纠错。我们会尽快核对公开来源，如有需要会通过你留下的邮箱联系你。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  再提交一条
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      称呼
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="方便我们回复你"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-type" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      类型
                    </label>
                    <select
                      id="contact-type"
                      name="type"
                      defaultValue="提供线索"
                      className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    >
                      <option value="提供线索">提供线索</option>
                      <option value="内容纠错">内容纠错</option>
                      <option value="媒体与合作">媒体与合作</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-target" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      涉及事件或链接
                    </label>
                    <input
                      id="contact-target"
                      name="target"
                      type="text"
                      placeholder="事件名称或公开链接（选填）"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-detail" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    详细说明
                  </label>
                  <textarea
                    id="contact-detail"
                    name="detail"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请尽量说明信息来源与关键时间点，最多 500 字"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  ></textarea>
                  <p className="mt-1.5 text-xs text-foreground-500">最多 500 字。</p>
                </div>

                <input
                  className="aux-field-layer"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && (
                  <p className="rounded-md border border-primary-300 bg-primary-50 px-3 py-2.5 text-sm text-primary-800">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <i className={status === "submitting" ? "ri-loader-4-line text-base animate-spin" : "ri-send-plane-line text-base"} aria-hidden="true"></i>
                  {status === "submitting" ? "正在提交…" : "提交给蝌蚪爆料"}
                </button>
                <p className="text-center text-xs text-foreground-500">
                  提交即表示你同意我们为核实线索而与您联系。
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}